import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { MonetaryAmount } from '../../generated/graphql';
import { AmountSumTableItem, Icon } from '../../UI';

export interface ClosedAccountItem {
  id: string;
  name: string;
  ownedValue: MonetaryAmount | null | undefined;
  closedAt?: string;
  // The route segment used to open the account detail side panel.
  accountType: string;
}

interface ClosedAccountsSectionProps {
  items: ClosedAccountItem[];
  isLoading?: boolean;
  onClickItem?: (item: ClosedAccountItem) => void;
}

export function ClosedAccountsSection({ items, isLoading, onClickItem }: ClosedAccountsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t: tAccountSideover } = useTranslation('accountSideover');

  if (!isLoading && items.length === 0) return null;

  return (
    <div className="mt-6">
      <button
        type="button"
        className="grid grid-cols-[1fr,40px] items-center w-full min-h-[40px]"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span className="text-darkMode-gray-medium font-bold text-lg text-left">
          {tAccountSideover('closed-accounts-section')}
        </span>
        <div className="flex items-center justify-center">
          <Icon
            icon="chevronDown"
            className={clsx('h-5 w-5 text-darkMode-gray-medium transition-transform duration-200', {
              '-rotate-90': !isExpanded,
            })}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-1 space-y-px">
              {items.map((item) => (
                <AmountSumTableItem
                  key={item.id}
                  title={item.name}
                  amount={item.ownedValue}
                  lastUpdated={item.closedAt}
                  lastUpdatedPrefix={tAccountSideover('closed')}
                  withChevron={!!onClickItem}
                  onClick={onClickItem ? () => onClickItem(item) : undefined}
                  className="focus:shadow-classic"
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
